/**
 * ParentMessagesHub — top-level "Messages" screen for parents.
 *
 * Unlike the per-child Messages tab (inside My Children), this shows
 * threads across ALL the parent's linked children, each labelled with
 * the child it concerns. It's the discoverable entry point reached
 * from the parent nav + dashboard.
 *
 * Reuses ComposeThread and ThreadView from ParentMessages.tsx.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import {
  MessageSquare,
  Plus,
  Loader2,
  ChevronLeft,
} from 'lucide-react';
import { db, auth } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { GradientHero, SoftCard, AccentButton } from '@/components/ui/glass';
import {
  ComposeThread,
  ThreadView,
  StatusBadge,
} from './ParentMessages';
import { CATEGORY_LABELS, type ParentMessageThread, type MessageCategory } from './types';

/** sessionStorage handoff key: another screen (e.g. the "add a child" reject)
 *  can pre-fill a new message and route the parent here. */
export const PARENT_COMPOSE_PREFILL_KEY = 'parentComposePrefill';
import type { Student } from '@/types';

export default function ParentMessagesHub() {
  const { students, subjects, classes } = useData();
  const { userData } = useAuth();

  const parentUid = userData?.uid ?? auth.currentUser?.uid ?? '';

  const linkedStudentIds = useMemo(
    () => userData?.parentAccount?.studentIds ?? [],
    [userData],
  );
  const linkedStudents = useMemo(
    () => students.filter((s) => linkedStudentIds.includes(s.id)),
    [students, linkedStudentIds],
  );

  const [threads, setThreads] = useState<ParentMessageThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'list' | 'pickChild' | 'compose' | 'thread'>('list');
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [composeChild, setComposeChild] = useState<Student | null>(null);
  const [prefill, setPrefill] = useState<{ category?: MessageCategory; subject?: string; body?: string } | null>(null);

  // Handoff: if another screen stashed a compose pre-fill, open compose with it.
  useEffect(() => {
    if (linkedStudents.length === 0) return; // wait for children to load
    const raw = sessionStorage.getItem(PARENT_COMPOSE_PREFILL_KEY);
    if (!raw) return;
    sessionStorage.removeItem(PARENT_COMPOSE_PREFILL_KEY);
    try {
      setPrefill(JSON.parse(raw));
      if (linkedStudents.length === 1) { setComposeChild(linkedStudents[0]); setView('compose'); }
      else setView('pickChild');
    } catch { /* ignore malformed handoff */ }
  }, [linkedStudents.length]);

  /* ---------- load all of this parent's threads ---------- */
  useEffect(() => {
    if (!parentUid) return;
    setLoading(true);
    const q = query(
      collection(db, 'reportParentMessages'),
      where('parentUid', '==', parentUid),
      orderBy('lastMessageAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const out: ParentMessageThread[] = [];
        snap.forEach((d) =>
          out.push({ id: d.id, ...(d.data() as Omit<ParentMessageThread, 'id'>) }),
        );
        setThreads(out);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, [parentUid]);

  const activeThread = useMemo(
    () => threads.find((t) => t.id === activeThreadId) ?? null,
    [threads, activeThreadId],
  );

  const childSubjects = useMemo(() => {
    if (!composeChild) return subjects;
    const ids = composeChild.subjects ?? [];
    const enrolled = subjects.filter((s) => ids.includes(s.id));
    return enrolled.length > 0 ? enrolled : subjects;
  }, [composeChild, subjects]);

  function classNameFor(currentClass?: string) {
    if (!currentClass) return '';
    const c = classes.find((cl) => cl.id === currentClass);
    return c ? c.name : currentClass;
  }

  function startCompose() {
    // If only one child, skip the picker
    if (linkedStudents.length === 1) {
      setComposeChild(linkedStudents[0]);
      setView('compose');
    } else {
      setView('pickChild');
    }
  }

  /* =============== child picker =============== */
  if (view === 'pickChild') {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setView('list')}
          className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 flex items-center gap-1"
        >
          <ChevronLeft size={16} /> Back to messages
        </button>
        <SoftCard>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
            Which child is this message about?
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {linkedStudents.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setComposeChild(s);
                  setView('compose');
                }}
                className="text-left px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-primary hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <p className="font-semibold text-slate-900 dark:text-slate-100">
                  {s.fullName}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Class {classNameFor(s.currentClass)}
                </p>
              </button>
            ))}
          </div>
        </SoftCard>
      </div>
    );
  }

  /* =============== compose =============== */
  if (view === 'compose' && composeChild) {
    return (
      <div className="space-y-4">
        <ComposeThread
          child={composeChild}
          subjects={childSubjects}
          parentUid={parentUid}
          parentName={userData?.displayName ?? 'Parent'}
          parentEmail={userData?.email ?? ''}
          initialCategory={prefill?.category}
          initialSubject={prefill?.subject}
          initialBody={prefill?.body}
          onCancel={() => { setView('list'); setPrefill(null); }}
          onCreated={(id) => {
            setPrefill(null);
            setActiveThreadId(id);
            setView('thread');
          }}
        />
      </div>
    );
  }

  /* =============== thread =============== */
  if (view === 'thread' && activeThread) {
    return (
      <ThreadView
        thread={activeThread}
        parentUid={parentUid}
        parentName={userData?.displayName ?? 'Parent'}
        onBack={() => setView('list')}
      />
    );
  }

  /* =============== list (all children) =============== */
  return (
    <div className="space-y-6">
      <GradientHero
        icon={MessageSquare}
        title="Messages"
        subtitle="Contact the school and track your conversations"
      />

      <div className="flex justify-end">
        <AccentButton onClick={startCompose}>
          <span className="flex items-center gap-2">
            <Plus size={16} /> New message
          </span>
        </AccentButton>
      </div>

      {loading ? (
        <SoftCard>
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 size={14} className="animate-spin" /> Loading messages…
          </div>
        </SoftCard>
      ) : threads.length === 0 ? (
        <SoftCard>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            You have no messages yet. Use “New message” to contact the school —
            to request a teacher consultation, query marks, flag an incorrect
            detail, or request a details update.
          </p>
        </SoftCard>
      ) : (
        <div className="space-y-2">
          {threads.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setActiveThreadId(t.id);
                setView('thread');
              }}
              className="w-full text-left"
            >
              <SoftCard className="transition-shadow hover:shadow-md">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {t.subject}
                      </span>
                      {t.unreadByParent && (
                        <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t.studentName} · {CATEGORY_LABELS[t.category]}
                    </p>
                    {t.lastMessagePreview && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 truncate">
                        {t.lastMessageSide === 'school' ? 'School: ' : 'You: '}
                        {t.lastMessagePreview}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={t.status} />
                </div>
              </SoftCard>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
